import { Col, Pagination, Popover, Row, Table, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import {
  exportLabelRankingCSV,
  exportLabelRankingExcel,
} from "radar/Service/RadarService";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RADAR_LABEL_RAKNING_FILTER } from "shared/constants/LocalStorageConsts";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import { useDispatch, useSelector } from "react-redux";
import { I18NColumn } from "../I18nColumn";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import { ptBRFormat } from "shared/helpers/BRLFormat";
import { FileType, downloadFile } from "shared/helpers/download-file";
import { ReactComponent as ExportIcon } from "shared/assets/images/cloud-download.svg";
import { getLabelRanking } from "explorer/reducers/rankings";

const formatDate = (date: string) => date && moment(date).format("DD/MM/YYYY");

const LabelsRankingTable: React.FC<any> = (props) => {
  const {
    filter,
    rankingIndex,
    setFilter,
    countries,
    history,
    setLoadingLabels,
  } = props;

  const [enableExport, setEnableExport] = useState<boolean>(false);
  const { t, i18n } = useTranslation("radar");
  const { labelsRanking, loading } = useSelector(
    (state: any) => state.rankings
  );
  const { genres } = useSelector((state: any) => state.genres);
  const dispatch = useDispatch();

  const MedianColumn = ({ value, median, valueText, medianText }: any) => {
    if (median === null) return value;

    return (
      <Popover
        overlayClassName="popover-country-genre"
        content={
          <div>
            <b>{valueText}</b> <br /> <span>{medianText}</span>
          </div>
        }
      >
        {value}
      </Popover>
    );
  };

  const getNumberPrecised = (number: number) =>
    (number || number === 0) && number.toPrecision(3);
  const colunas: ColumnsType<any> = [
    {
      dataIndex: "position",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.LabelsRanking.labelName"),
      dataIndex: "labelName",
      width: "20%",
    },
    {
      title: t("pages.ArtistsRanking.genres"),
      dataIndex: "genresStrm",
      width: "10%",
      align: "center",
      render: (value) => (
        <I18NColumn
          listRow={value ? [...value].sort() : []}
          label={t("pages.ArtistsRanking.genres")}
          i18nFunction={(genre: string) => getI18nGenre(genre, i18n, genres)}
          t={t}
        />
      ),
    },
    {
      title: t("pages.ArtistsRanking.country"),
      dataIndex: "country",
      width: "10%",
      align: "center",
      render: (value) => (
        <I18NColumn
          listRow={value ? [...value].sort() : []}
          label={t("pages.ArtistsRanking.country")}
          i18nFunction={(country: string) =>
            getI18nCoutry(country, i18n, countries)
          }
          t={t}
        />
      ),
    },
    {
      title: t("pages.LabelsRanking.popularityIndex"),
      dataIndex: "popularityIndex",
      align: "center",
      render: (value, record) => (
        <MedianColumn
          value={getNumberPrecised(value)}
          median={record.popularityIndexAvg}
          valueText={t("pages.LabelsRanking.popularityTooltip", {
            value: getNumberPrecised(value),
          })}
          medianText={t("pages.LabelsRanking.popularityTooltipMedian", {
            median: getNumberPrecised(record.popularityIndexAvg),
          })}
        />
      ),
      width: "10%",
    },
    {
      title: t("pages.LabelsRanking.concentrationIndex"),
      dataIndex: "concentrationIndex",
      align: "center",
      width: "10%",
      render: (value, record) => (
        <MedianColumn
          value={getNumberPrecised(value)}
          median={record.concentrationIndexAvg}
          valueText={t("pages.LabelsRanking.concentrationTooltip", {
            value: getNumberPrecised(value),
          })}
          medianText={t("pages.LabelsRanking.concentrationTooltipMedian", {
            median: getNumberPrecised(record.popularityIndexAvg),
          })}
        />
      ),
    },
    {
      title: t("pages.LabelsRanking.albumCount"),
      dataIndex: "albumCount",
      align: "center",
      width: "10%",
      render: (value, record) => (
        <MedianColumn
          value={value}
          median={record.albumCountTotal}
          valueText={t("pages.LabelsRanking.releaseTooltip", { value: value })}
          medianText={t("pages.LabelsRanking.releaseTooltipTotal", {
            total: record.albumCountTotal,
          })}
        />
      ),
    },
    {
      title: t("pages.LabelsRanking.lastYearReleases"),
      dataIndex: "lastYearReleases",
      align: "center",
      width: "10%",
      render: (value, record) => (
        <MedianColumn
          value={value}
          median={record.lastYearReleasesTotal}
          valueText={t("pages.LabelsRanking.releaseLastYearTooltip", {
            value: value,
          })}
          medianText={t("pages.LabelsRanking.releaseLastYearTooltipTotal", {
            total: record.lastYearReleasesTotal,
          })}
        />
      ),
    },
    {
      title: t("pages.LabelsRanking.artistCount"),
      dataIndex: "artistCount",
      align: "center",
      width: "10%",
      render: (value, record) => (
        <MedianColumn
          value={value}
          median={record.artistCountTotal}
          valueText={t("pages.LabelsRanking.artistsTooltip", {
            value: value,
          })}
          medianText={t("pages.LabelsRanking.artistsTooltipTotal", {
            total: record.artistCountTotal,
          })}
        />
      ),
    },
  ];

  const downloadRankingReportCSV = async () => {
    setLoadingLabels(true);
    const combinedFilter = {
      ...filter,
      SortMode: rankingIndex,
    };

    const data: any = await exportLabelRankingCSV(combinedFilter);
    downloadFile(data, "labels-ranking.csv", FileType.Csv);
    setLoadingLabels(false);
    setEnableExport(false);
  };

  const downloadRankingReportExcel = async () => {
    setLoadingLabels(true);
    const combinedFilter = {
      ...filter,
      SortMode: rankingIndex,
    };

    const data: any = await exportLabelRankingExcel(combinedFilter);
    downloadFile(data, "labels-ranking.xlsx", FileType.Xlsx);
    setLoadingLabels(false);
    setEnableExport(false);
  };

  useEffect(() => {
    if (filter.artistEnd > 0)
      dispatch(
        getLabelRanking({
          filter,
          rankingIndex,
        })
      );
  }, [rankingIndex, filter, dispatch]);

  const onRowClick = (data: any) => {
    localStorage.setItem("RADAR_SELECTED_LABEL", data.labelName);
    history.push("/label-profile");
  };

  return (
    <>
      <Row justify="end" style={{ gap: 12 }}>
        {enableExport && (
          <div className="container-botoes-exportar">
            <button
              className="button-export"
              onClick={() => downloadRankingReportCSV()}
            >
              CSV
            </button>
            <button
              className="button-export"
              onClick={() => downloadRankingReportExcel()}
            >
              Excel
            </button>
          </div>
        )}
        <button
          className="botao-azul button-export"
          onClick={() => setEnableExport(!enableExport)}
        >
          {t("pages.ArtistProfile.export")}
          <ExportIcon className="download-icon" />
        </button>
      </Row>
      <Row style={{ marginTop: 16 }}>
        <Col xs={12}>
          <Typography.Title
            level={4}
            style={{ color: "white", textAlign: "left" }}
          >
            {t("pages.LabelsRanking.results", {
              total: ptBRFormat(labelsRanking[`${rankingIndex}`]?.total || 0),
            })}
          </Typography.Title>
        </Col>
        <Col xs={12}>
          <Typography.Title
            level={5}
            style={{ color: "white", textAlign: "right" }}
          >
            {t("pages.ArtistsRanking.lastUpdate")}
            <b>
              {`${
                formatDate(labelsRanking[`${rankingIndex}`]?.rankingDate) || ""
              } `}
            </b>
          </Typography.Title>
        </Col>

        <Col xs={24} style={{ marginTop: 30 }}>
          <Table
            columns={colunas}
            dataSource={labelsRanking[`${rankingIndex}`]?.labels || []}
            rowKey="labelName"
            loading={loading}
            rowClassName="clickable-row"
            pagination={false}
            onRow={(data) => ({ onClick: () => onRowClick(data) })}
          />
        </Col>
        <Col xs={24}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              style={{ alignSelf: "flex-end", margin: "16px" }}
              hideOnSinglePage={true}
              showSizeChanger={false}
              onChange={(page) => {
                const saveFilter = {
                  ...filter,
                  offset: page * 10 - 10,
                  limit: 10,
                };

                localStorage.setItem(
                  RADAR_LABEL_RAKNING_FILTER,
                  JSON.stringify(saveFilter)
                );
                setFilter(saveFilter);
              }}
              current={filter.offset / filter.limit + 1}
              pageSize={filter.limit}
              total={labelsRanking[`${rankingIndex}`]?.total}
            />
          </div>
        </Col>
      </Row>
    </>
  );
};

export default LabelsRankingTable;
